// src/utils/microphonePermission.ts
//
// Cross-browser microphone permission detection + request flow.
//
// Why a dedicated helper:
//   - The Permissions API (`navigator.permissions.query({ name: 'microphone' })`)
//     is the modern way to inspect mic state without prompting, but Safari
//     historically rejected `'microphone'` as an unknown PermissionName.
//   - `navigator.mediaDevices` itself is undefined in non-secure contexts
//     (`http://` over LAN, embedded WebViews) and in jsdom test envs.
//   - We never want the device hook to throw an unhandled exception when
//     it tries to detect permissions on a quirky browser.
//
// All four functions in this file are SAFE — they catch every browser
// quirk and return a structured `MicPermissionState` enum that the
// device hook (and the readiness UI) can switch on.
//
// PHASE 2 SCOPE
//   The Twilio Device hook only needs to know the current permission
//   state and to be able to PROMPT for it. We do not yet need device
//   selection (input/output picker) or volume metering — those are
//   Phase 3 active-call-shell concerns.

/**
 * Discrete microphone permission states. The device hook composes
 * this with its own state machine — see `useTwilioVoiceDevice`.
 */
export type MicPermissionState =
  /** Browser environment cannot access mic at all (no mediaDevices, no secure context, jsdom test). */
  | 'unsupported'
  /** Permission status not yet checked. Initial state. */
  | 'unknown'
  /** User has not yet been prompted; we can call `requestMicrophonePermission()`. */
  | 'prompt'
  /** User has granted permission. We can construct a Device. */
  | 'granted'
  /** User has explicitly denied permission. UI must prompt them to fix it via browser settings. */
  | 'denied';

/**
 * Inspect the current microphone permission state WITHOUT triggering
 * a permission prompt. Safe to call on every render.
 *
 * Returns 'unsupported' on:
 *   - non-secure contexts (Twilio Voice SDK requires HTTPS or localhost)
 *   - environments without `navigator.mediaDevices`
 *   - jsdom (used by vitest)
 */
export async function getMicrophonePermissionState(): Promise<MicPermissionState> {
  // Bail if we have no mediaDevices at all (jsdom, embedded WebViews,
  // or http:// over LAN). Twilio Voice SDK won't work in those envs anyway.
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.mediaDevices === 'undefined' ||
    typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    return 'unsupported';
  }

  // Try the Permissions API first — this is the only way to inspect
  // mic state without prompting. Safari supports it from 16.0+; older
  // Safari and embedded WebViews throw a TypeError on the unknown
  // PermissionName, which we catch and treat as 'prompt' (the safest
  // default — the user can still grant it via the request flow).
  if (typeof navigator.permissions !== 'undefined' && typeof navigator.permissions.query === 'function') {
    try {
      // The cast through `unknown` is required because the lib.dom
      // types narrow `name` to a small enum that doesn't always
      // include 'microphone' even on browsers that support it.
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      switch (status.state) {
        case 'granted':
          return 'granted';
        case 'denied':
          return 'denied';
        case 'prompt':
          return 'prompt';
        default:
          return 'unknown';
      }
    } catch {
      // Older Safari throws on `'microphone'` — fall through.
    }
  }

  // Fallback: we know we have mediaDevices but no Permissions API.
  // We can't inspect state without prompting, so return 'prompt' so
  // the UI knows it's safe to call requestMicrophonePermission().
  return 'prompt';
}

/**
 * Trigger the browser permission prompt by calling `getUserMedia`. We
 * immediately stop the resulting tracks because we only want the
 * permission, not the audio stream itself — Twilio Voice SDK takes its
 * own getUserMedia call when `Device.connect()` runs.
 *
 * Returns the resolved permission state. Errors are mapped:
 *   - NotAllowedError / SecurityError → 'denied'
 *   - NotFoundError / OverconstrainedError → 'unsupported' (no mic hardware)
 *   - everything else → re-thrown so callers can surface the raw error
 */
export async function requestMicrophonePermission(): Promise<MicPermissionState> {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.mediaDevices === 'undefined' ||
    typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    return 'unsupported';
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Immediately release the tracks — we only wanted the permission.
    stream.getTracks().forEach((t) => t.stop());
    return 'granted';
  } catch (err) {
    if (err && typeof err === 'object' && 'name' in err) {
      const name = (err as { name: string }).name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        return 'denied';
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return 'unsupported';
      }
    }
    throw err;
  }
}

/**
 * Subscribe to permission state changes (e.g. user toggles mic in
 * browser settings while the app is running). Returns an unsubscribe
 * function. No-op + returns a no-op cleanup if the Permissions API
 * isn't available — callers should poll on user interaction in that case.
 */
export async function subscribeToMicrophonePermission(
  onChange: (state: MicPermissionState) => void
): Promise<() => void> {
  if (typeof navigator === 'undefined' || typeof navigator.permissions === 'undefined') {
    return () => {};
  }

  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    const handler = () => {
      switch (status.state) {
        case 'granted':
          onChange('granted');
          break;
        case 'denied':
          onChange('denied');
          break;
        case 'prompt':
          onChange('prompt');
          break;
        default:
          onChange('unknown');
      }
    };
    status.addEventListener('change', handler);
    return () => status.removeEventListener('change', handler);
  } catch {
    return () => {};
  }
}
